import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Bell, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button } from '@/components/ui/button';
import { Hint } from '@/components/ui/hint';
import { alertLine } from '@/components/alerts/alert-line';
import { useSessionClaims } from '@/auth/session-claims';
import { cn } from '@/lib/cn';
import { queryClient } from '@/lib/query-client';
import { ME_QUERY_KEY, type OperatorMe } from '@/lib/me/api';
import type { IOperatorAlertV1 } from '@/lib/alerts/alert.types';
import { alertView } from '@/lib/alerts/alert.view';
import { useAlertsStore } from '@/lib/alerts/alerts.store';
import { refreshOsNotify, setOsNotify, useOsNotifyStore } from '@/lib/os-notify/os-notify';
import { useConnectionStore } from '@/state/connection.store';
import {
  alertTarget,
  bellAlerts,
  bellAlertsUnread,
  loadInbox,
  readAlert,
  readAlertsShown,
  seedInbox,
  useNotificationsStore,
  type IBellAlert,
} from '@/state/notifications.store';
import { disruptionEntry } from '@/routes/disruptions/disruption-entry';
import type { Notification, NotificationTone } from '@/lib/ws/notification-feed';

// The console's ONE bell (NEX-296 / NEX-299 item 5; operator-notifications U2). Two feeds,
// one list, one badge (state/notifications.store.ts):
//   · the run notifications of the WS-event feed, as before;
//   · the operator alerts in the operator's airports — the inbox (`GET /v1/alerts`, read
//     ONCE per opening and once on the socket's first connect, never on a timer) merged
//     with what arrives live (U1's alerts store).
// Opening it reads what it shows (the server cursor moves to the newest alert shown, once
// the read is back); «Marcar todo como leído» does the same at any time; clicking an alert
// reads THAT one and goes where U1 sends it («Operar» / «Ver vuelo»). The operator's own
// actions never count. The OS-notification opt-in lives here too (lib/os-notify).
//
// One place per screen: the top bar on every console page (phone included), the foot of
// the icon rail on «Operar» — where there is no top bar — taking the place of U1's rail
// counter (one badge, not two). The list is NOT portalled (brief §6 inv. 8): it opens from
// its own button, inside the chrome, and keeps clear of the rehearsal strip at the top.
// Radix's menu gives arrow keys, Esc and the focus back on the bell.

const DOT: Readonly<Record<NotificationTone | IOperatorAlertV1['severity'], string>> = {
  critical: 'bg-destructive',
  error: 'bg-destructive',
  warning: 'bg-primary',
  success: 'bg-primary/50',
  info: 'bg-muted-foreground/50',
};

const ROW =
  'flex w-full cursor-pointer select-none items-start gap-2 px-3 py-2 text-left outline-none ' +
  'focus:bg-accent focus:text-accent-foreground';
const NOTE = 'px-3 py-3 text-center text-sm text-muted-foreground';

/** Clear of the rehearsal strip (≈ 25 px) at the top; the viewport's edge elsewhere. */
const COLLISION = { top: 40, right: 8, bottom: 8, left: 8 };

type Row = { at: number; alert: IBellAlert } | { at: number; run: Notification };

function relativeTime(at: number, t: TFunction<'admin'>): string {
  const secs = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (secs < 60) return t('time.secondsAgo', { n: secs });
  const mins = Math.round(secs / 60);
  if (mins < 60) return t('time.minutesAgo', { n: mins });
  const hrs = Math.round(mins / 60);
  return t('time.hoursAgo', { n: hrs });
}

export function NotificationBell({ rail = false }: { rail?: boolean }) {
  const { t } = useTranslation();
  // The relative-time words live in the `admin` chrome bundle; an alert's line in `alerts`.
  const { t: tAdmin } = useTranslation('admin');
  const { t: tAlerts } = useTranslation('alerts');
  const navigate = useNavigate();
  const claimed = useSessionClaims()?.operatorUrn ?? undefined;
  const me = queryClient.getQueryData<OperatorMe>(ME_QUERY_KEY)?.operatorUrn ?? claimed;
  const runs = useNotificationsStore((s) => s.notifications);
  const runUnread = useNotificationsStore((s) => s.unread);
  const inbox = useNotificationsStore((s) => s.inbox);
  const readIds = useNotificationsStore((s) => s.readIds);
  const entries = useAlertsStore((s) => s.entries);
  const liveRead = useAlertsStore((s) => s.readUpTo);
  const alerts = useMemo(
    () => bellAlerts({ inbox, readIds }, { entries, readUpTo: liveRead }, me),
    [inbox, readIds, entries, liveRead, me],
  );
  const count =
    runUnread + bellAlertsUnread(alerts, { inbox, readIds }, { entries, readUpTo: liveRead });
  const [open, setOpen] = useState(false);
  // What was unread when it was read by opening: it stays marked while the list is open.
  const [fresh, setFresh] = useState<readonly string[]>([]);
  const isOpen = useRef(false);
  const errorId = useId();

  // ONE seed read on the socket's first connect (the store keeps it to once per page).
  useEffect(() => {
    const seed = (): void => {
      if (useConnectionStore.getState().connected) seedInbox(me);
    };
    seed();
    return useConnectionStore.subscribe(seed);
  }, [me]);

  const keep = (ids: readonly string[]): void => setFresh((f) => [...f, ...ids]);
  const read = (): void => {
    if (isOpen.current) keep(readAlertsShown(me));
  };
  const reload = (): void => {
    void loadInbox(me).then((ok) => (ok ? read() : undefined));
  };

  const onOpenChange = (next: boolean): void => {
    setOpen(next);
    isOpen.current = next;
    if (!next) {
      setFresh([]);
      return;
    }
    refreshOsNotify();
    useNotificationsStore.getState().markAllRead();
    reload();
  };

  const readAll = (): void => {
    useNotificationsStore.getState().markAllRead();
    keep(readAlertsShown(me));
  };

  const goAlert = (a: IOperatorAlertV1): void => {
    readAlert(a.id);
    const to = alertTarget(a);
    if (to) void navigate(to);
  };

  const goRun = (n: Notification): void => {
    if (n.caseUrn) {
      void navigate(`/cases/${encodeURIComponent(n.caseUrn)}`);
    } else if (n.disruptionUrn) {
      // A disruption notification opens the disruption's default view.
      const entry = disruptionEntry(n.disruptionUrn);
      void navigate(entry.to, { state: entry.state });
    }
  };

  const rows: Row[] = [
    ...alerts.map((alert) => ({ at: Date.parse(alert.alert.at), alert })),
    ...runs.map((run) => ({ at: run.at, run })),
  ].sort((x, y) => y.at - x.at);
  const label = count ? t('bell.label', { count }) : t('topbar.notifications');

  const trigger = (
    <Menu.Trigger asChild>
      <Button
        variant="ghost"
        size="icon"
        className={cn('relative shrink-0', rail ? 'h-11 w-11' : 'h-9 w-9')}
        aria-label={label}
        data-testid={rail ? 'rail-bell' : 'topbar-bell'}
      >
        <Bell className={rail ? 'h-5 w-5' : 'h-4 w-4'} aria-hidden />
        {count > 0 ? (
          <span
            aria-hidden
            data-testid="notification-unread-badge"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
          >
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </Button>
    </Menu.Trigger>
  );

  return (
    <Menu.Root open={open} onOpenChange={onOpenChange}>
      {rail ? (
        <Hint body={label} side="right">
          {trigger}
        </Hint>
      ) : (
        trigger
      )}
      <Menu.Content
        side={rail ? 'right' : 'bottom'}
        align="end"
        sideOffset={rail ? 8 : 4}
        collisionPadding={COLLISION}
        data-testid="notification-menu"
        aria-busy={inbox.status === 'loading'}
        className="z-50 flex max-h-[min(32rem,var(--radix-dropdown-menu-content-available-height))] w-[min(22rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md"
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-1">
          <Menu.Label className="py-1 text-sm font-semibold">
            {t('topbar.notifications')}
          </Menu.Label>
          {rows.length > 0 ? (
            <Menu.Item
              onSelect={(e) => {
                e.preventDefault();
                readAll();
              }}
              className="inline-flex min-h-8 cursor-pointer items-center gap-1 rounded-sm px-2 text-xs text-muted-foreground outline-none hover:text-foreground focus:bg-accent focus:text-accent-foreground"
              data-testid="bell-read-all"
            >
              <Check className="h-3 w-3" aria-hidden />
              {t('topbar.markAllRead')}
            </Menu.Item>
          ) : null}
        </div>
        <OsNotifyToggle />
        {/* the list's ONE scroll region (frontend-conventions §18.1) */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {inbox.status === 'loading' ? (
            <p className={cn(NOTE, 'motion-safe:animate-pulse')} data-testid="bell-loading">
              {t('bell.loading')}
            </p>
          ) : null}
          {inbox.status === 'error' ? (
            <div className="flex items-center gap-2 px-3 py-2" data-testid="bell-error">
              <p id={errorId} className="min-w-0 flex-1 text-sm">
                {t('bell.error')}
              </p>
              <Menu.Item
                aria-describedby={errorId}
                onSelect={(e) => {
                  e.preventDefault();
                  reload();
                }}
                className="inline-flex min-h-8 cursor-pointer items-center rounded-sm border border-border px-2 text-xs outline-none focus:bg-accent focus:text-accent-foreground"
              >
                {t('bell.retry')}
              </Menu.Item>
            </div>
          ) : null}
          {rows.length === 0 && inbox.status !== 'loading' ? (
            <p className={NOTE} data-testid="bell-empty">
              {t('topbar.noNotifications')}
            </p>
          ) : null}
          {rows.length > 0 ? (
            <Menu.Group className="divide-y divide-border" data-testid="notification-list">
              {rows.map((row) =>
                'alert' in row ? (
                  <AlertRow
                    key={row.alert.alert.id}
                    item={row.alert}
                    fresh={fresh.includes(row.alert.alert.id)}
                    ago={relativeTime(row.at, tAdmin)}
                    tAlerts={tAlerts}
                    unreadLabel={t('bell.unread')}
                    onGo={goAlert}
                  />
                ) : (
                  // Focusable even without a target (a screen reader reaches every row);
                  // choosing one with nowhere to go keeps the list open.
                  <Menu.Item
                    key={row.run.id}
                    onSelect={(e) =>
                      row.run.caseUrn || row.run.disruptionUrn ? goRun(row.run) : e.preventDefault()
                    }
                    className={cn(
                      ROW,
                      !(row.run.caseUrn || row.run.disruptionUrn) && 'cursor-default',
                    )}
                  >
                    <span
                      className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', DOT[row.run.tone])}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="truncate text-sm font-medium">{row.run.title}</span>
                        <span className="shrink-0 text-[10px] text-muted-foreground">
                          {relativeTime(row.at, tAdmin)}
                        </span>
                      </span>
                      {row.run.description ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {row.run.description}
                        </span>
                      ) : null}
                    </span>
                  </Menu.Item>
                ),
              )}
            </Menu.Group>
          ) : null}
        </div>
      </Menu.Content>
    </Menu.Root>
  );
}

/** One operator alert: its concrete line, where it leads, how long ago; unread is bold. */
function AlertRow({
  item,
  fresh,
  ago,
  tAlerts,
  unreadLabel,
  onGo,
}: {
  item: IBellAlert;
  fresh: boolean;
  ago: string;
  tAlerts: TFunction;
  unreadLabel: string;
  onGo: (a: IOperatorAlertV1) => void;
}) {
  const { alert } = item;
  const unread = item.unread || fresh;
  const v = alertView(alert);
  return (
    <Menu.Item
      onSelect={() => onGo(alert)}
      className={cn(ROW, unread && 'bg-primary/5')}
      data-testid="bell-alert"
      data-unread={unread ? 'true' : undefined}
    >
      <span
        className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', DOT[alert.severity])}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn('block text-sm leading-snug', unread ? 'font-semibold' : 'font-medium')}
        >
          {unread ? <span className="sr-only">{`${unreadLabel}: `}</span> : null}
          {alertLine(tAlerts, alert)}
        </span>
        <span className="block text-xs text-muted-foreground">
          {[v.operate ? tAlerts('operate') : v.flight ? tAlerts('viewFlight') : '', ago]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>
    </Menu.Item>
  );
}

/**
 * The OS-notification opt-in (lib/os-notify): asks the browser on THIS click only. Absent
 * where the browser has no notifications; disabled, saying why, once the browser denied.
 */
function OsNotifyToggle() {
  const { t } = useTranslation();
  const optIn = useOsNotifyStore((s) => s.optIn);
  const permission = useOsNotifyStore((s) => s.permission);
  if (permission === 'unsupported') return null;
  const denied = permission === 'denied';
  return (
    <Menu.CheckboxItem
      checked={optIn && permission === 'granted'}
      disabled={denied}
      onSelect={(e) => e.preventDefault()}
      onCheckedChange={(on) => void setOsNotify(on)}
      className={cn(
        ROW,
        'shrink-0 items-center border-b border-border text-xs text-muted-foreground',
      )}
      data-testid="os-notify-toggle"
    >
      <span
        aria-hidden
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-input"
      >
        <Menu.ItemIndicator>
          <Check className="h-3 w-3" />
        </Menu.ItemIndicator>
      </span>
      {denied ? t('bell.osNotifyDenied') : t('bell.osNotify')}
    </Menu.CheckboxItem>
  );
}
